import React, { useMemo } from 'react';

import { BottomActionLayout } from '@/components/shared/BottomActionLayout/BottomActionLayout';
import { AsaphButton } from '@/components/shared/Button';
import { IndexIndicator } from '@/components/shared/IndexIndicator';
import { FormInput } from '@/components/shared/Inputs';
import { Blurb } from '@/components/shared/Inputs/Blurb';
import { Label } from '@/components/shared/Inputs/Label';
import { Select } from '@/components/shared/Inputs/Select';
import {
	DAYS_OF_WEEK,
	normalizeServiceSize,
	SERVICE_SIZE_OPTIONS,
} from '@/config/constants';
import {
	useCreateTeamServiceMutation,
	useUpdateTeamServiceMutation,
} from '@/graphql';
import { classNames } from '@/helpers/classNames';
import { hr24to12 } from '@/helpers/timeFormatter';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import { useAnalyticsEvent } from '@/providers/AnalyticsProvider';
import { useServices } from '@/providers/ServicesProvider';
import { useTeam } from '@/providers/TeamProvider';
import { format } from 'date-fns';
import { Formik } from 'formik';
import * as Yup from 'yup';

import { OnboardingContext } from './Onboarding';
import styles from './Onboarding.module.scss';

type AddServiceFormSchema = {
	serviceName: string;
	serviceDay: number;
	startTime: string;
	serviceSize: string;
};

const formValidationSchema: Yup.SchemaOf<AddServiceFormSchema> =
	Yup.object().shape({
		serviceName: Yup.string()
			.required('Service name is required')
			.min(1, 'Service name is required')
			.max(100, 'Service name can be 100 characters'),
		serviceDay: Yup.number()
			.required('Service day is required')
			.min(0, 'Select a day')
			.max(6, 'Select a day'),
		startTime: Yup.string().required('Start time is required'),
		serviceSize: Yup.string().required('Service size is required'),
	});

const normalizeStartAt = (startAt: string): string => {
	if (!startAt) return '09:00';
	const trimmed = startAt.trim();
	if (trimmed.length >= 5) return trimmed.slice(0, 5);
	return trimmed;
};

const StepFive = () => {
	const { setStep, step, shouldAddService, completedStepIndices } =
		React.useContext(OnboardingContext);
	const { services, refetch: refetchServices } = useServices();
	const { team, isLoading: isTeamLoading } = useTeam();
	const { mutateAsync: createService } = useCreateTeamServiceMutation();
	const { mutateAsync: updateService } = useUpdateTeamServiceMutation();
	const { handleError } = useErrorHandler();
	const fireSaveService = useAnalyticsEvent('Onboarding_Step5_Save_Service');
	const fireUpdateService = useAnalyticsEvent(
		'Onboarding_Step5_Update_Service',
	);
	const fireSkipService = useAnalyticsEvent('Onboarding_Step5_Skip_Service');

	const existingService = services[0];
	const serviceAlreadyAdded = !!existingService;
	// A lone team member is the person currently completing onboarding, so a
	// service someone else on the team already set up doesn't apply here -
	// let them edit it. While the team is still loading, default to locked
	// so we don't briefly show an editable form for an existing team.
	const isSoleTeamMember = !isTeamLoading && team.members.length <= 1;
	const isServiceLocked = serviceAlreadyAdded && !isSoleTeamMember;

	const startTimeItems = useMemo(() => {
		const items: { label: string; value: string }[] = [];
		for (let h = 6; h <= 23; h++) {
			for (let m = 0; m < (h === 23 ? 15 : 60); m += 15) {
				const value = format(new Date(0, 0, 0, h, m), 'HH:mm');
				items.push({
					label: hr24to12(value),
					value,
				});
			}
		}
		return items;
	}, []);

	const serviceDayItems = useMemo(
		() =>
			DAYS_OF_WEEK.map((day, index) => ({
				label: day,
				value: index,
			})),
		[],
	);

	const initialValues: AddServiceFormSchema = useMemo(
		() =>
			existingService
				? {
						serviceName: existingService.name || 'Mornings',
						serviceDay: existingService.day ?? 0,
						startTime: normalizeStartAt(existingService.startAt),
						serviceSize: normalizeServiceSize(existingService.size),
				  }
				: {
						serviceName: 'Mornings',
						serviceDay: 0,
						startTime: '09:00',
						serviceSize: 'small',
				  },
		[existingService],
	);

	return (
		<Formik
			initialValues={initialValues}
			enableReinitialize
			validateOnMount
			validationSchema={isServiceLocked ? undefined : formValidationSchema}
			onSubmit={async (values) => {
				if (isServiceLocked) {
					fireSkipService();
					setStep(step + 1);
					return;
				}
				try {
					if (serviceAlreadyAdded) {
						await updateService({
							serviceId: existingService.id,
							input: {
								name: values.serviceName.trim(),
								day: values.serviceDay,
								startAt: values.startTime,
								size: values.serviceSize,
							},
						});
						await refetchServices();
						fireUpdateService({ serviceSize: values.serviceSize });
					} else {
						await createService({
							input: {
								name: values.serviceName.trim(),
								day: values.serviceDay,
								startAt: values.startTime,
								size: values.serviceSize,
							},
						});
						await refetchServices();
						fireSaveService({ serviceSize: values.serviceSize });
					}
					setStep(step + 1);
				} catch (error) {
					handleError({
						error: error as Error,
						errorCode: 'CreateTeamServiceError',
						friendlyMessage: 'Failed to add service',
					});
				}
			}}
		>
			{({
				handleSubmit,
				handleChange,
				handleBlur,
				setFieldValue,
				setFieldTouched,
				values,
				errors,
				touched,
				isValid,
			}) => (
				<BottomActionLayout
					className={classNames([styles['layout']])}
					listClassName={styles['step-five']}
					contentClassName={styles['form']}
					topContent={
						<IndexIndicator
							className={styles['indicator']}
							dotClassName={styles['dot']}
							total={6}
							current={3}
							variant="stretched"
							onIndicatorClick={(i) => setStep(i + 2)}
							hasHover
							conditionalItems={[
								{ index: 3, visible: shouldAddService },
							]}
							disabledItems={[{ index: 5, enabled: false }]}
							hasDataItems={completedStepIndices}
						/>
					}
					actionContent={
						<>
							<AsaphButton
								color="grey"
								label="Back"
								onClick={() => setStep(step - 1)}
							/>
							<AsaphButton
								color="blue"
								label="Next"
								disabled={isServiceLocked ? false : !isValid}
								onClick={() => handleSubmit()}
							/>
						</>
					}
				>
					<div className={styles['title-header']}>
						<div className={styles['heading']}>Add a service</div>
						<div className={classNames([styles['text'], 'mt-sm'])}>
							{serviceAlreadyAdded
								? 'You already have a service.'
								: 'More than one? You can add more later.'}
						</div>
					</div>

					{isServiceLocked ? (
						<Label>Service name</Label>
					) : (
						<Label>
							Service name{' '}
							<span
								style={{
									color: 'var(--px-color-error, #e53e3e)',
								}}
							>
								*
							</span>
						</Label>
					)}
					<FormInput
						placeholder="e.g. Mornings"
						name="serviceName"
						id="serviceName"
						autoComplete="off"
						value={values.serviceName}
						onChange={handleChange}
						onBlur={handleBlur}
						error={!!(touched.serviceName && errors.serviceName)}
						disabled={isServiceLocked}
					/>
					{touched.serviceName && errors.serviceName && (
						<Blurb isError>{errors.serviceName}</Blurb>
					)}
					<div className="p-sm" />

					{isServiceLocked ? (
						<Label>Service day</Label>
					) : (
						<Label>
							Service day{' '}
							<span
								style={{
									color: 'var(--px-color-error, #e53e3e)',
								}}
							>
								*
							</span>
						</Label>
					)}
					<Select
						placeholder="Select day"
						items={serviceDayItems}
						onChange={(value) => {
							if (typeof value === 'number') {
								setFieldValue('serviceDay', value);
								setFieldTouched('serviceDay', true);
							}
						}}
						value={values.serviceDay}
						error={!!(touched.serviceDay && errors.serviceDay)}
						disabled={isServiceLocked}
					/>
					{touched.serviceDay && errors.serviceDay && (
						<Blurb isError>{errors.serviceDay}</Blurb>
					)}
					<div className="p-sm" />

					{isServiceLocked ? (
						<Label>Start time</Label>
					) : (
						<Label>
							Start time{' '}
							<span
								style={{
									color: 'var(--px-color-error, #e53e3e)',
								}}
							>
								*
							</span>
						</Label>
					)}
					<Select
						placeholder="Select time"
						items={startTimeItems}
						onChange={(value) => {
							if (typeof value === 'string') {
								setFieldValue('startTime', value);
								setFieldTouched('startTime', true);
							}
						}}
						value={values.startTime}
						error={!!(touched.startTime && errors.startTime)}
						disabled={isServiceLocked}
					/>
					{touched.startTime && errors.startTime && (
						<Blurb isError>{errors.startTime}</Blurb>
					)}
					<div className="p-sm" />

					{isServiceLocked ? (
						<Label
							tooltipHeading="Service size"
							tooltip="This helps us tailor insights and recommendations to your congregation size."
						>
							Service size
						</Label>
					) : (
						<Label
							tooltipHeading="Service size"
							tooltip="This helps us tailor insights and recommendations to your congregation size."
						>
							Service size{' '}
							<span
								style={{
									color: 'var(--px-color-error, #e53e3e)',
								}}
							>
								*
							</span>
						</Label>
					)}
					<Select
						placeholder="Select size"
						items={SERVICE_SIZE_OPTIONS}
						onChange={(value) => {
							if (typeof value === 'string') {
								setFieldValue('serviceSize', value);
								setFieldTouched('serviceSize', true);
							}
						}}
						value={values.serviceSize}
						error={!!(touched.serviceSize && errors.serviceSize)}
						disabled={isServiceLocked}
					/>
					{touched.serviceSize && errors.serviceSize && (
						<Blurb isError>{errors.serviceSize}</Blurb>
					)}
				</BottomActionLayout>
			)}
		</Formik>
	);
};

export default StepFive;
